import React from 'react';

const Resume: React.FC = () => {
  const experience = [
    {
      id: 0,
      title: "Software Engineer",
      company: "KPMG",
      period: "January 2026 - Present",
      description: "Full-time Software Engineer developing Tax technology on KPMG's tech platform.",
      technologies: ["Microsoft Azure", ".NET Core", "DevOps", "SQL", "CI/CD"]
    },
    {
      id: 1,
      title: "Computer Science Graduate Student",
      company: "Colorado School of Mines",
      period: "August 2024 - December 2025",
      description: "Conducted research as part of a Cryptography course on a PRNG combining cosmic ray data with AI-generated entropy, applying SHA3/HMAC for enhanced security. More recently, conducted research as a part of an AI Ethics course in Human-Centered AI, exploring ethical design and trust in AI systems.",
      technologies: ["Python", "Cryptography", "Human-Centered AI", "Data Analysis"]
    },
    {
      id: 2,
      title: "Software Engineer Intern",
      company: "Embross Airport Services",
      period: "June 2023 - August 2023",
      description: "Contributed to development of passenger processing and self-service kiosk software for the aviation industry, onsite in Bergerac, France, as the sole intern. Designed and tested backend features to improve performance, reliability, and user experience. Collaborated with senior engineers on debugging, integration, and deployment of production systems.",
      technologies: ["Object Oriented Programming", "Java", "JavaScript", "Angular", "Android Development"]
    }
  ];

  const education = [
    {
      id: 1,
      degree: "Master of Science in Computer Science",
      school: "Colorado School of Mines",
      period: "August 2024 - December 2025",
      description: "GPA: 3.46. Relevant coursework in AI Ethics, Full-Stack LLMs, Advanced Machine Learning, Theory of Cryptography, Information Security and Privacy, Advanced Game Development."
    },
    {
      id: 2,
      degree: "Bachelor of Science in Computer Science",
      school: "Colorado School of Mines",
      period: "August 2021 - December 2024",
      description: "Relevant coursework in Algorithms, Data Structures, ML and AI, Data Science, and Software Engineering."
    }
  ];

  const skills = {
    "Programming Languages": ["Python", "Java", "C/C++", "C#", "SQL", "JavaScript", "TypeScript"],
    "Tools & Frameworks": ["Git", "FastAPI", "Angular", "Docker", "JUnit", "Mockito", "Android Studio", "Android SDK"],
    "Data & ML": ["NumPy", "Pandas", "scikit-learn", "Neural Networks", "LLMs", "Data Pipelines", "Machine Learning"],
    "Technologies": ["Linux", "RESTful APIs", "PostgreSQL", "CI/CD", "JSON"],
    "Foundational Knowledge": ["Cryptography", "AI Ethics", "Software Engineering", "Computer Architecture", "Algorithms", "Game Development"]
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-6 rounded">
        <p className="text-yellow-800 text-center font-medium">Currently employed</p>
      </div>

      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Resume</h1>
        <p className="text-xl text-gray-600">My professional experience and qualifications</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Experience */}
        <div className="lg:col-span-2">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Experience</h2>
          <div className="space-y-6">
            {experience.map((job) => (
              <div key={job.id} className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                    <p className="text-blue-600 font-medium">{job.company}</p>
                  </div>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {job.period}
                  </span>
                </div>
                <p className="text-gray-700 mb-3">{job.description}</p>
                <div className="flex flex-wrap gap-2">
                  {job.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skills */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Skills</h2>
          <div className="space-y-6">
            {Object.entries(skills).map(([category, skillList]) => (
              <div key={category} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{category}</h3>
                <div className="flex flex-wrap gap-2">
                  {skillList.map((skill) => (
                    <span
                      key={skill}
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Education */}
      <div className="mt-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Education</h2>
        <div className="space-y-6">
          {education.map((edu) => (
            <div key={edu.id} className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{edu.degree}</h3>
                  <p className="text-blue-600 font-medium">{edu.school}</p>
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {edu.period}
                </span>
              </div>
              <p className="text-gray-700">{edu.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Download Resume Button */}
      <div className="text-center mt-12">
        <a
          href="/documents/Nicholas_Iverson_Resume.pdf"
          download
          className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
        >
          Download Resume (PDF)
        </a>
      </div>
    </div>
  );
};

export default Resume; 
